import type { SectionFormHandle } from "../components/personal-sheet/edit";
import type { PersonalSheet } from "../types/personalSheet";

type SectionRef = { current: SectionFormHandle | null };

export interface SectionRefs {
  generalRef: SectionRef;
  contactsRef: SectionRef;
  addressesRef: SectionRef;
  emergencyRef: SectionRef;
  idDocumentsRef: SectionRef;
  bankingRef: SectionRef;
  educationRef: SectionRef;
  experienceRef: SectionRef;
  languagesRef: SectionRef;
  scientificWorksRef: SectionRef;
  governmentAwardsRef: SectionRef;
  socialCategoriesRef: SectionRef;
}

export function buildMergedSheet(baseSheet: PersonalSheet, refs: SectionRefs): PersonalSheet {
  const generalChanges = refs.generalRef.current?.getChanges() as Record<string, unknown> | null;
  const contactsChanges = refs.contactsRef.current?.getChanges() as Record<string, unknown> | null;
  const addressesChanges = refs.addressesRef.current?.getChanges() as Record<string, unknown> | null;
  const idDocChanges = refs.idDocumentsRef.current?.getChanges() as Record<string, unknown> | null;
  const bankingChanges = refs.bankingRef.current?.getChanges() as Record<string, unknown> | null;
  const emergencyChanges = refs.emergencyRef.current?.getChanges() as unknown as PersonalSheet["emergency_contacts"] | null;
  const educationChanges = refs.educationRef.current?.getChanges() as unknown as PersonalSheet["education"] | null;
  const experienceChanges = refs.experienceRef.current?.getChanges() as unknown as PersonalSheet["experience"] | null;
  const socialChanges = refs.socialCategoriesRef.current?.getChanges() as unknown as PersonalSheet["social_categories"] | null;
  const languageChanges = refs.languagesRef.current?.getChanges() as unknown as PersonalSheet["languages"] | null;
  const scientificChanges = refs.scientificWorksRef.current?.getChanges() as unknown as PersonalSheet["scientific_works"] | null;
  const awardsChanges = refs.governmentAwardsRef.current?.getChanges() as unknown as PersonalSheet["government_awards"] | null;

  const flagRefs = [
    refs.educationRef,
    refs.experienceRef,
    refs.scientificWorksRef,
    refs.governmentAwardsRef,
    refs.socialCategoriesRef,
  ];
  const flagChanges = flagRefs.reduce<Record<string, boolean>>(function collectFlags(acc, ref) {
    const flags = ref.current?.getFlagChanges?.();
    return flags ? { ...acc, ...flags } : acc;
  }, {});

  return {
    ...baseSheet,
    general: { ...baseSheet.general, ...(generalChanges ?? {}), ...flagChanges } as PersonalSheet["general"],
    contacts:
      baseSheet.contacts || contactsChanges
        ? ({ ...(baseSheet.contacts ?? {}), ...(contactsChanges ?? {}) } as PersonalSheet["contacts"])
        : null,
    addresses:
      baseSheet.addresses || addressesChanges
        ? ({ ...(baseSheet.addresses ?? {}), ...(addressesChanges ?? {}) } as PersonalSheet["addresses"])
        : null,
    id_documents:
      baseSheet.id_documents || idDocChanges
        ? ({ ...(baseSheet.id_documents ?? {}), ...(idDocChanges ?? {}) } as PersonalSheet["id_documents"])
        : null,
    banking:
      baseSheet.banking || bankingChanges
        ? ({ ...(baseSheet.banking ?? {}), ...(bankingChanges ?? {}) } as PersonalSheet["banking"])
        : null,
    emergency_contacts: Array.isArray(emergencyChanges) ? emergencyChanges : baseSheet.emergency_contacts,
    education: Array.isArray(educationChanges) ? educationChanges : baseSheet.education,
    experience: Array.isArray(experienceChanges) ? experienceChanges : baseSheet.experience,
    social_categories: Array.isArray(socialChanges) ? socialChanges : baseSheet.social_categories,
    languages: Array.isArray(languageChanges) ? languageChanges : baseSheet.languages,
    scientific_works: Array.isArray(scientificChanges) ? scientificChanges : baseSheet.scientific_works,
    government_awards: Array.isArray(awardsChanges) ? awardsChanges : baseSheet.government_awards,
  };
}
